import React from 'react';
import { AlertCircle, Building2, GraduationCap } from 'lucide-react';

const Pathways = () => {
  return (
    <div className="min-h-screen pt-32">
      {/* Hero */}
      <section className="py-20">
        <div className="container-custom">
          <h1 className="heading-1 mb-8">PROFESSIONAL PATHWAYS</h1>
          <p className="body-large max-w-3xl">
            Understanding the professional ecosystem around the GPS Method™ and pathways to representation.
          </p>
        </div>
      </section>

      {/* Important Context */}
      <section className="py-20 bg-card">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="bg-page border border-primary p-8 rounded-sm mb-12">
              <div className="flex items-start gap-4">
                <AlertCircle size={24} className="text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="heading-5 mb-3 text-primary">Important Clarification</h3>
                  <p className="body-small mb-4">
                    Training at the GPS Coaching School does not guarantee professional representation.
                  </p>
                  <p className="body-small text-secondary">
                    Representation is independent, selective, and assessed separately from training completion.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="heading-2 mb-8">THE PROFESSIONAL ECOSYSTEM</h2>
            <p className="body-medium mb-6">
              The GPS Coaching School operates within a wider professional ecosystem.
            </p>
            <p className="body-medium text-secondary">
              Advanced students may be guided toward industry pathways, including agency and management structures, where appropriate.
            </p>
          </div>
        </div>
      </section>

      {/* Two Paths */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="heading-2 mb-16 text-center">TWO DISTINCT PATHWAYS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card-standard p-8">
              <div className="flex justify-center mb-6">
                <GraduationCap size={48} className="text-primary" />
              </div>
              <h3 className="heading-4 mb-4 text-primary text-center">GPS Coaching School</h3>
              <p className="body-small text-center mb-6">
                Training & Formation
              </p>
              <ul className="space-y-3">
                <li className="body-small text-secondary">• Professional training in the GPS Method™</li>
                <li className="body-small text-secondary">• Long-term development programs</li>
                <li className="body-small text-secondary">• Direct guidance from Method Director</li>
                <li className="body-small text-secondary">• Focus: Internal development and readiness</li>
              </ul>
            </div>

            <div className="card-standard p-8">
              <div className="flex justify-center mb-6">
                <Building2 size={48} className="text-primary" />
              </div>
              <h3 className="heading-4 mb-4 text-primary text-center">Woodhouse & Archer</h3>
              <p className="body-small text-center mb-6">
                Representation & Professional Management
              </p>
              <ul className="space-y-3">
                <li className="body-small text-secondary">• Independent agency / academy partner</li>
                <li className="body-small text-secondary">• Selective professional representation</li>
                <li className="body-small text-secondary">• Career management for established talent</li>
                <li className="body-small text-secondary">• Focus: Industry placement and professional work</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How They Relate */}
      <section className="py-20 bg-card">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-2 mb-8">HOW THEY RELATE</h2>
            <div className="space-y-6">
              <p className="body-medium">
                The GPS Coaching School and Woodhouse & Archer operate as separate entities with different functions.
              </p>
              <p className="body-medium text-secondary">
                Training at the GPS School focuses on internal development and professional readiness. Representation through Woodhouse & Archer focuses on career management and industry placement.
              </p>
              <p className="body-medium text-secondary">
                Advanced GPS students who demonstrate exceptional readiness may be considered for representation. This is assessed independently and is not automatic.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Assessment Process */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-2 mb-8">PROFESSIONAL READINESS ASSESSMENT</h2>
            <p className="body-medium mb-6">
              For students interested in professional representation, readiness is assessed through:
            </p>
            <div className="space-y-4">
              <div className="border-l-2 border-primary pl-6">
                <h3 className="heading-6 mb-2 text-primary">Training Completion</h3>
                <p className="body-small text-secondary">Full completion of GPS training program with demonstrated competency.</p>
              </div>
              <div className="border-l-2 border-primary pl-6">
                <h3 className="heading-6 mb-2 text-primary">Professional Standards</h3>
                <p className="body-small text-secondary">Assessment of professional readiness, not just technical ability.</p>
              </div>
              <div className="border-l-2 border-primary pl-6">
                <h3 className="heading-6 mb-2 text-primary">Independent Review</h3>
                <p className="body-small text-secondary">Evaluation by Woodhouse & Archer representatives, separate from training assessment.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ethical Notice */}
      <section className="py-20 bg-card">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-2 mb-8">ETHICAL CLARITY</h2>
            <p className="body-medium mb-6">
              We do not promise industry access. We do not guarantee representation. We do not sell shortcuts.
            </p>
            <p className="body-medium text-secondary">
              What we offer is training. What you do with that training is determined by your readiness, commitment, and the independent assessment of professional representatives.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container-custom text-center">
          <h2 className="heading-3 mb-8 text-secondary">BEGIN WITH TRAINING</h2>
          <p className="body-medium mb-12 max-w-2xl mx-auto text-secondary">
            Professional pathways begin with serious training. Start with the GPS Coaching School.
          </p>
          <a href="/school" className="btn-primary inline-flex items-center">
            View Training Programs
          </a>
        </div>
      </section>
    </div>
  );
};

export default Pathways;